import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import Modal from 'react-modal';
import './AdminPage.css';
import logo from './logoBranca.png';
import { FadeIn, AnimatedButton, Particles } from './components/magicui';
import { LogoutIcon, RefreshIcon, BackIcon, UserIcon, SettingsIcon, KeyIcon, LockIcon, UnlockIcon } from './components/PastelGlyphIcons';
import { sanitizeEmail } from './utils/sanitize';

Modal.setAppElement('#root');

function AdminPage({ userRole, onLogout }) {
    const navigate = useNavigate();

    // Estados principais
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // Estados dos modais
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    // Estados dos formulários
    const [createForm, setCreateForm] = useState({
        email: '',
        password: '',
        role: 'user',
        regionals: [],
        active: true
    });

    const [editForm, setEditForm] = useState({
        email: '',
        role: 'user',
        regionals: [],
        active: true
    });

    const [passwordForm, setPasswordForm] = useState({
        newPassword: '',
        confirmPassword: ''
    });

    // Lista de regionais disponíveis
    const allRegionals = [
        { value: 'CR', label: 'CR - Caruaru' },
        { value: 'GR', label: 'GR - Garanhuns' },
        { value: 'PT', label: 'PT - Petrolina' },
        { value: 'ST', label: 'ST - Serra Talhada' },
        { value: 'AV', label: 'AV - Arcoverde' },
        { value: 'RF', label: 'RF - Recife' },
        { value: 'OR', label: 'OR - Ouricuri' },
    ];

    // Verificar se usuário é master
    useEffect(() => {
        if (userRole !== 'master') {
            navigate('/postes');
        }
    }, [userRole, navigate]);

    // Buscar todos os usuários
    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .order('email');

            if (error) {
                setError('Erro ao carregar lista de usuários.');
                return;
            }

            setUsers(data || []);
        } catch (err) {
            setError('Erro inesperado ao carregar usuários.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Limpar mensagens após tempo
    useEffect(() => {
        if (error || successMessage) {
            const timer = setTimeout(() => {
                setError('');
                setSuccessMessage('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error, successMessage]);

    // Criar novo usuário
    const handleCreateUser = async (e) => {
        e.preventDefault();

        // Validar e sanitizar email
        const sanitizedEmail = sanitizeEmail(createForm.email);
        if (!sanitizedEmail) {
            setError('Email inválido.');
            return;
        }

        if (createForm.password !== passwordForm.confirmPassword) {
            setError('As senhas não coincidem.');
            return;
        }

        if (createForm.password.length < 6) {
            setError('A senha deve ter pelo menos 6 caracteres.');
            return;
        }

        setLoading(true);

        try {
            // Criar usuário na autenticação
            const { data: authData, error: authError } = await supabase.auth.signUp({
                email: sanitizedEmail,
                password: createForm.password,
            });

            if (authError) {
                setError('Erro ao criar usuário. Verifique os dados e tente novamente.');
                return;
            }

            // Criar perfil
            const { error: profileError } = await supabase
                .from('profiles')
                .insert([{
                    id: authData.user.id,
                    email: sanitizedEmail,
                    role: createForm.role,
                    regionals: createForm.regionals,
                    active: createForm.active
                }]);

            if (profileError) {
                setError('Erro ao criar perfil do usuário.');
                return;
            }

            setSuccessMessage('Usuário criado com sucesso!');
            setShowCreateModal(false);
            resetCreateForm();
            fetchUsers();

        } catch (err) {
            setError('Erro inesperado ao criar usuário.');
        } finally {
            setLoading(false);
        }
    };

    // Editar usuário
    const handleEditUser = async (e) => {
        e.preventDefault();

        // Validar e sanitizar email
        const sanitizedEmail = sanitizeEmail(editForm.email);
        if (!sanitizedEmail) {
            setError('Email inválido.');
            return;
        }

        setLoading(true);

        try {
            const { error } = await supabase
                .from('profiles')
                .update({
                    email: sanitizedEmail,
                    role: editForm.role,
                    regionals: editForm.regionals,
                    active: editForm.active
                })
                .eq('id', selectedUser.id);

            if (error) {
                setError('Erro ao atualizar usuário.');
                return;
            }

            setSuccessMessage('Usuário atualizado com sucesso!');
            setShowEditModal(false);
            setSelectedUser(null);
            fetchUsers();

        } catch (err) {
            setError('Erro inesperado ao atualizar usuário.');
        } finally {
            setLoading(false);
        }
    };

    // Alterar senha
    const handleChangePassword = async (e) => {
        e.preventDefault();

        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            setError('As senhas não coincidem.');
            return;
        }

        if (passwordForm.newPassword.length < 6) {
            setError('A senha deve ter pelo menos 6 caracteres.');
            return;
        }

        setLoading(true);

        try {
            // Note: Esta funcionalidade requer admin API do Supabase
            // Para implementação completa, seria necessário um endpoint backend
            setError('Funcionalidade de alteração de senha requer implementação backend com Admin API.');

        } catch (err) {
            setError('Erro ao alterar senha.');
        } finally {
            setLoading(false);
        }
    };

    // Toggle ativo/inativo
    const toggleUserActive = async (user) => {
        const confirmMessage = user.active
            ? `Desabilitar usuário ${user.email}? Ele não conseguirá mais acessar o sistema.`
            : `Reabilitar usuário ${user.email}?`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        setLoading(true);

        try {
            const { error } = await supabase
                .from('profiles')
                .update({ active: !user.active })
                .eq('id', user.id);

            if (error) {
                setError('Erro ao alterar status do usuário.');
                return;
            }

            setSuccessMessage(`Usuário ${user.active ? 'desabilitado' : 'reabilitado'} com sucesso!`);
            fetchUsers();

        } catch (err) {
            setError('Erro inesperado ao alterar status.');
        } finally {
            setLoading(false);
        }
    };

    // Funções auxiliares
    const resetCreateForm = () => {
        setCreateForm({
            email: '',
            password: '',
            role: 'user',
            regionals: [],
            active: true
        });
        setPasswordForm({
            newPassword: '',
            confirmPassword: ''
        });
    };

    const openEditModal = (user) => {
        setSelectedUser(user);
        setEditForm({
            email: user.email,
            role: user.role || 'user',
            regionals: user.regionals || [],
            active: user.active
        });
        setShowEditModal(true);
    };

    const openPasswordModal = (user) => {
        setSelectedUser(user);
        setPasswordForm({
            newPassword: '',
            confirmPassword: ''
        });
        setShowPasswordModal(true);
    };

    const handleRegionalChange = (regional, isChecked, formType = 'create') => {
        const targetForm = formType === 'create' ? createForm : editForm;
        const setTargetForm = formType === 'create' ? setCreateForm : setEditForm;

        const newRegionals = isChecked
            ? [...targetForm.regionals, regional]
            : targetForm.regionals.filter(r => r !== regional);

        setTargetForm(prev => ({
            ...prev,
            regionals: newRegionals
        }));
    };

    return (
        <div className="admin-page">
            <Particles className="absolute inset-0 z-0" quantity={35} color="#ffffff" size={0.9} speed={0.6} />

            <div className="admin-container relative z-10">
                {/* Header */}
                <FadeIn delay={0} className="admin-header">
                    <div className="header-content">
                            <img src={logo} alt="Logo" className="admin-logo relative z-10" />
                        <div className="header-text">
                            <h1>
                                Administração de Usuários
                            </h1>
                            <FadeIn delay={1} className="p">
                                Gerenciar contas de usuário do sistema
                            </FadeIn>
                        </div>
                    </div>
                    <FadeIn delay={2} className="header-buttons">
                        <AnimatedButton onClick={onLogout} className="logout-button">
                            <LogoutIcon size={16} style={{marginRight: '6px'}} />
                            Sair
                        </AnimatedButton>
                    </FadeIn>
                </FadeIn>

                {/* Barra de Controles */}
                <FadeIn delay={3} className="controls-bar">
                    <div className="stats-section">
                        <div className="stat-box">
                            <span className="stat-number">{users.length}</span>
                            <span className="stat-label">Total</span>
                        </div>
                        <div className="stat-box">
                            <span className="stat-number">{users.filter(u => u.active).length}</span>
                            <span className="stat-label">Ativos</span>
                        </div>
                        <div className="stat-box">
                            <span className="stat-number">{users.filter(u => !u.active).length}</span>
                            <span className="stat-label">Inativos</span>
                        </div>
                    </div>

                    <div className="actions-section">
                            <AnimatedButton
                                onClick={() => setShowCreateModal(true)}
                                className="btn btn-primary"
                            >
                                <UserIcon size={16} style={{marginRight: '6px'}} />
                                Novo Usuário
                            </AnimatedButton>
                        <AnimatedButton
                            onClick={fetchUsers}
                            className="btn btn-secondary"
                            disabled={loading}
                        >
                            <RefreshIcon size={16} style={{marginRight: '6px'}} />
                            Atualizar
                        </AnimatedButton>
                        <AnimatedButton
                            onClick={() => navigate('/postes')}
                            className="btn btn-secondary"
                        >
                            <BackIcon size={16} style={{marginRight: '6px'}} />
                            Voltar
                        </AnimatedButton>
                    </div>
                </FadeIn>

                {/* Mensagens */}
                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}

                {successMessage && (
                    <div className="success-message">
                        {successMessage}
                    </div>
                )}

                {/* Conteúdo Principal */}
                <FadeIn delay={4} className="content-section">
                    {loading && (
                        <div className="loading-state">
                            <div className="loading-spinner"></div>
                            <p>Carregando usuários...</p>
                        </div>
                    )}

                    {!loading && (
                        <div className="table-wrapper">
                            <table className="users-table">
                                <thead>
                                    <tr>
                                        <th>Email</th>
                                        <th>Perfil</th>
                                        <th>Regionais</th>
                                        <th>Status</th>
                                        <th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((user) => (
                                        <tr key={user.id} className={!user.active ? 'inactive-user' : ''}>
                                            <td className="email-cell">{user.email}</td>
                                            <td className="role-cell">
                                                <span className={`role-badge ${user.role}`}>
                                                    {user.role === 'master' ? 'Master' : 'Usuário'}
                                                </span>
                                            </td>
                                            <td className="regionals-cell">
                                                {user.regionals?.length > 0 ? user.regionals.join(', ') : 'Nenhuma'}
                                            </td>
                                            <td className="status-cell">
                                                <span className={`status-badge ${user.active ? 'active' : 'inactive'}`}>
                                                    {user.active ? 'Ativo' : 'Inativo'}
                                                </span>
                                            </td>
                                            <td className="actions-cell">
                                                <button
                                                    onClick={() => openEditModal(user)}
                                                    className="btn btn-small btn-secondary"
                                                    title="Editar usuário"
                                                >
                                                    <SettingsIcon size={14} />
                                                </button>
                                                <button
                                                    onClick={() => openPasswordModal(user)}
                                                    className="btn btn-small btn-secondary"
                                                    title="Alterar senha"
                                                >
                                                    <KeyIcon size={14} />
                                                </button>
                                                <button
                                                    onClick={() => toggleUserActive(user)}
                                                    className={`btn btn-small ${user.active ? 'btn-warning' : 'btn-success'}`}
                                                    title={user.active ? 'Desabilitar' : 'Reabilitar'}
                                                >
                                                    {user.active ? <LockIcon size={14} /> : <UnlockIcon size={14} />}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {users.length === 0 && !loading && (
                                <div className="empty-state">
                                    <p className="empty-message">Nenhum usuário encontrado.</p>
                                </div>
                            )}
                        </div>
                    )}
                </FadeIn>

                {/* Modal de Criação */}
                <Modal
                    isOpen={showCreateModal}
                    onRequestClose={() => setShowCreateModal(false)}
                    className="admin-modal"
                    overlayClassName="admin-modal-overlay"
                >
                    <div className="modal-header">
                        <h2>Criar Novo Usuário</h2>
                        <button onClick={() => setShowCreateModal(false)} className="modal-close">✖</button>
                    </div>

                    <form onSubmit={handleCreateUser} className="modal-form">
                        <div className="form-group">
                            <label>Email *</label>
                            <input
                                type="email"
                                value={createForm.email}
                                onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                                required
                                className="form-input"
                            />
                        </div>

                        <div className="form-group">
                            <label>Senha *</label>
                            <input
                                type="password"
                                value={createForm.password}
                                onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
                                required
                                minLength={6}
                                className="form-input"
                            />
                        </div>

                        <div className="form-group">
                            <label>Confirmar Senha *</label>
                            <input
                                type="password"
                                value={passwordForm.confirmPassword}
                                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                                required
                                minLength={6}
                                className="form-input"
                            />
                        </div>

                        <div className="form-group">
                            <label>Perfil</label>
                            <select
                                value={createForm.role}
                                onChange={(e) => setCreateForm(prev => ({ ...prev, role: e.target.value }))}
                                className="form-select"
                            >
                                <option value="user">Usuário</option>
                                <option value="master">Master</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Regionais</label>
                            <div className="regionals-grid">
                                {allRegionals.map(regional => (
                                    <label key={regional.value} className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={createForm.regionals.includes(regional.value)}
                                            onChange={(e) => handleRegionalChange(regional.value, e.target.checked, 'create')}
                                        />
                                        {regional.label}
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={createForm.active}
                                    onChange={(e) => setCreateForm(prev => ({ ...prev, active: e.target.checked }))}
                                />
                                Usuário ativo
                            </label>
                        </div>

                        <div className="modal-actions">
                            <button type="button" onClick={() => setShowCreateModal(false)} className="btn btn-secondary">
                                Cancelar
                            </button>
                            <button type="submit" disabled={loading} className="btn btn-primary">
                                {loading ? '⏳ Criando...' : '✨ Criar Usuário'}
                            </button>
                        </div>
                    </form>
                </Modal>

                {/* Modal de Edição */}
                <Modal
                    isOpen={showEditModal}
                    onRequestClose={() => setShowEditModal(false)}
                    className="admin-modal"
                    overlayClassName="admin-modal-overlay"
                >
                    <div className="modal-header">
                        <h2>Editar Usuário</h2>
                        <button onClick={() => setShowEditModal(false)} className="modal-close">✖</button>
                    </div>

                    <form onSubmit={handleEditUser} className="modal-form">
                        <div className="form-group">
                            <label>Email *</label>
                            <input
                                type="email"
                                value={editForm.email}
                                onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                                required
                                className="form-input"
                            />
                        </div>

                        <div className="form-group">
                            <label>Perfil</label>
                            <select
                                value={editForm.role}
                                onChange={(e) => setEditForm(prev => ({ ...prev, role: e.target.value }))}
                                className="form-select"
                            >
                                <option value="user">Usuário</option>
                                <option value="master">Master</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Regionais</label>
                            <div className="regionals-grid">
                                {allRegionals.map(regional => (
                                    <label key={regional.value} className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={editForm.regionals.includes(regional.value)}
                                            onChange={(e) => handleRegionalChange(regional.value, e.target.checked, 'edit')}
                                        />
                                        {regional.label}
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={editForm.active}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, active: e.target.checked }))}
                                />
                                Usuário ativo
                            </label>
                        </div>

                        <div className="modal-actions">
                            <button type="button" onClick={() => setShowEditModal(false)} className="btn btn-secondary">
                                Cancelar
                            </button>
                            <button type="submit" disabled={loading} className="btn btn-primary">
                                {loading ? '⏳ Atualizando...' : '💫 Atualizar'}
                            </button>
                        </div>
                    </form>
                </Modal>

                {/* Modal de Alteração de Senha */}
                <Modal
                    isOpen={showPasswordModal}
                    onRequestClose={() => setShowPasswordModal(false)}
                    className="admin-modal"
                    overlayClassName="admin-modal-overlay"
                >
                    <div className="modal-header">
                        <h2>🔐 Alterar Senha</h2>
                        <button onClick={() => setShowPasswordModal(false)} className="modal-close">✖</button>
                    </div>

                    <form onSubmit={handleChangePassword} className="modal-form">
                        <div className="form-group">
                            <label>Usuário</label>
                            <input
                                type="text"
                                value={selectedUser?.email || ''}
                                disabled
                                className="form-input"
                            />
                        </div>

                        <div className="form-group">
                            <label>Nova Senha *</label>
                            <input
                                type="password"
                                value={passwordForm.newPassword}
                                onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                                required
                                minLength={6}
                                className="form-input"
                            />
                        </div>

                        <div className="form-group">
                            <label>Confirmar Nova Senha *</label>
                            <input
                                type="password"
                                value={passwordForm.confirmPassword}
                                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                                required
                                minLength={6}
                                className="form-input"
                            />
                        </div>

                        <div className="modal-actions">
                            <button type="button" onClick={() => setShowPasswordModal(false)} className="btn btn-secondary">
                                Cancelar
                            </button>
                            <button type="submit" disabled={loading} className="btn btn-primary">
                                {loading ? '⏳ Alterando...' : '🔐 Alterar Senha'}
                            </button>
                        </div>
                    </form>
                </Modal>
            </div>
        </div>
    );
}

export default AdminPage;